import { UI_CONFIG } from '../config.js';
import { XRPanel } from './XRPanel.js';

const AUDIO_ACTIONS = ['audio-play', 'audio-pause', 'audio-stop'];

function drawWrappedText(context, text, x, y, maxWidth, lineHeight, maxLines) {
  const paragraphs = String(text ?? '').split('\n');
  let lineIndex = 0;

  for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex += 1) {
    const words = paragraphs[paragraphIndex].trim().split(/\s+/);
    let line = '';

    for (let wordIndex = 0; wordIndex < words.length; wordIndex += 1) {
      const candidate = line ? `${line} ${words[wordIndex]}` : words[wordIndex];

      if (context.measureText(candidate).width > maxWidth && line) {
        context.fillText(line, x, y + lineIndex * lineHeight);
        lineIndex += 1;
        line = words[wordIndex];

        if (lineIndex >= maxLines) {
          return lineIndex;
        }
      } else {
        line = candidate;
      }
    }

    if (line && lineIndex < maxLines) {
      context.fillText(line, x, y + lineIndex * lineHeight);
      lineIndex += 1;
    }

    if (paragraphIndex < paragraphs.length - 1 && lineIndex < maxLines) {
      lineIndex += 0.55;
    }
  }

  return lineIndex;
}

/**
 * audioController contract:
 *   play(url, hotspotData), pause(), stop()
 * None of these methods are called until the visitor presses a panel button.
 */
export class InfoPanel extends XRPanel {
  constructor({
    audioController = null,
    onReset = null,
    onClose = null,
    onOpen = null,
    distance = UI_CONFIG.panelDistance,
    width = UI_CONFIG.panelWidth,
    height = UI_CONFIG.panelHeight,
    canvasWidth = UI_CONFIG.panelCanvasWidth,
  } = {}) {
    super({
      distance: distance ?? 1.7,
      width: width ?? 1.32,
      height: height ?? 0.82,
      canvasWidth: canvasWidth ?? 1280,
    });

    this.name = 'info-panel';
    this.audioController = audioController;
    this.onReset = onReset;
    this.onClose = onClose;
    this.onOpen = onOpen;
    this.hotspot = null;
    this.audioState = 'idle';
    this._audioOperationToken = 0;

    this.setButtons([
      { action: 'close', label: '×', x: 0.918, y: 0.045, width: 0.055, height: 0.085 },
      { action: 'audio-play', label: 'REPRODUCIR', x: 0.055, y: 0.795, width: 0.17, height: 0.105 },
      { action: 'audio-pause', label: 'PAUSAR', x: 0.24, y: 0.795, width: 0.145, height: 0.105 },
      { action: 'audio-stop', label: 'DETENER', x: 0.4, y: 0.795, width: 0.145, height: 0.105 },
      {
        action: 'reset',
        label: 'VOLVER AL INICIO',
        x: 0.67,
        y: 0.795,
        width: 0.275,
        height: 0.105,
        style: 'primary',
      },
    ]);

    this._setAudioButtonsEnabled(false);
    this.redraw();
  }

  setAudioController(audioController) {
    this.audioController = audioController;
    this._setAudioButtonsEnabled(Boolean(
      (this.hotspot?.audio || this.hotspot?.narration) && audioController?.play,
    ));
  }

  open(hotspot, camera) {
    const data = hotspot?.data ?? hotspot;
    if (!data) {
      return false;
    }

    if (this.isOpen && this.hotspot?.id !== data.id) {
      this._stopAudio();
    }

    this.hotspot = data;
    this.audioState = 'idle';
    this._setAudioButtonsEnabled(Boolean(
      (data.audio || data.narration) && this.audioController?.play,
    ));
    this.redraw();
    this.show(camera);
    this.onOpen?.(data, this);
    return true;
  }

  close({ stopAudio = true } = {}) {
    if (stopAudio) {
      this._stopAudio();
    }
    this.hide();
    this.onClose?.(this.hotspot, this);
  }

  handleAction(action) {
    const normalizedAction = action === 'start' ? 'reset' : action;
    if (!this.isActionEnabled(normalizedAction)) {
      return false;
    }

    switch (normalizedAction) {
      case 'close':
        this.close();
        return true;
      case 'audio-play':
        this._playAudio();
        return true;
      case 'audio-pause':
        this._pauseAudio();
        return true;
      case 'audio-stop':
        this._stopAudio();
        return true;
      case 'reset':
        this._stopAudio();
        this.hide();
        this.onReset?.();
        return true;
      default:
        return super.handleAction(action);
    }
  }

  _setAudioButtonsEnabled(enabled) {
    for (let index = 0; index < AUDIO_ACTIONS.length; index += 1) {
      this.setButtonEnabled(AUDIO_ACTIONS[index], enabled);
    }
  }

  _playAudio() {
    if ((!this.hotspot?.audio && !this.hotspot?.narration) || !this.audioController?.play) {
      return;
    }

    try {
      const operationToken = ++this._audioOperationToken;
      const result = this.audioState === 'paused' && this.audioController.resume
        ? this.audioController.resume()
        : this.audioController.play(this.hotspot.audio, this.hotspot);
      this._setAudioState(this.audioController.state ?? 'playing');

      if (result?.catch) {
        result.catch(() => {
          if (operationToken === this._audioOperationToken) {
            this._setAudioState('error');
          }
        });
      }
    } catch (error) {
      console.error('No se ha podido reproducir el audio del hotspot.', error);
      this.audioState = 'error';
      this.redraw();
    }
  }

  _pauseAudio() {
    if (!this.audioController?.pause) {
      return;
    }
    this.audioController.pause();
    this._setAudioState(this.audioController.state ?? 'paused');
  }

  _stopAudio() {
    this._audioOperationToken += 1;
    if (this.audioController?.stop) {
      this.audioController.stop();
    }
    this._setAudioState('idle');
  }

  _setAudioState(state) {
    const normalizedState = state === 'stopped' ? 'idle' : state;
    if (this.audioState !== normalizedState) {
      this.audioState = normalizedState;
      this.redraw();
    }
  }

  update(camera) {
    super.update(camera);

    if (this.isOpen
      && (this.hotspot?.audio || this.hotspot?.narration)
      && this.audioController?.state) {
      this._setAudioState(this.audioController.state);
    }
  }

  drawContent(context, width, height) {
    const hotspot = this.hotspot;

    context.fillStyle = '#f3c96b';
    context.font = `700 ${Math.round(height * 0.025)}px system-ui, -apple-system, sans-serif`;
    context.textAlign = 'left';
    context.textBaseline = 'alphabetic';
    context.fillText('PUNTO DE INTERÉS', width * 0.057, height * 0.102);

    context.fillStyle = '#fffaf0';
    context.font = `700 ${Math.round(height * 0.071)}px Georgia, serif`;
    drawWrappedText(
      context,
      hotspot?.title ?? 'Información de la visita',
      width * 0.057,
      height * 0.2,
      width * 0.81,
      height * 0.076,
      2,
    );

    context.strokeStyle = 'rgba(243, 201, 107, 0.38)';
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(width * 0.057, height * 0.315);
    context.lineTo(width * 0.943, height * 0.315);
    context.stroke();

    context.fillStyle = 'rgba(255, 250, 240, 0.91)';
    context.font = `400 ${Math.round(height * 0.037)}px system-ui, -apple-system, sans-serif`;
    drawWrappedText(
      context,
      hotspot?.description
        ?? 'Contenido de ejemplo pendiente de revisión historiográfica.',
      width * 0.057,
      height * 0.385,
      width * 0.886,
      height * 0.054,
      6,
    );

    context.fillStyle = this.hotspot?.audio || this.hotspot?.narration
      ? 'rgba(173, 224, 226, 0.85)'
      : 'rgba(195, 202, 203, 0.58)';
    context.font = `600 ${Math.round(height * 0.024)}px system-ui, -apple-system, sans-serif`;
    context.fillText(this._getAudioStatusText(), width * 0.057, height * 0.75);
  }

  _getAudioStatusText() {
    if (!this.hotspot?.audio && !this.hotspot?.narration) {
      return 'Este punto no tiene audio asociado.';
    }

    switch (this.audioState) {
      case 'playing':
        return 'Narración en reproducción';
      case 'paused':
        return 'Narración en pausa';
      case 'loading':
        return 'Cargando narración…';
      case 'error':
        return 'No se ha podido reproducir la narración.';
      default:
        return 'Narración disponible · se inicia únicamente al pulsar Reproducir';
    }
  }

  dispose() {
    this._stopAudio();
    super.dispose();
  }
}

export default InfoPanel;
